import moment from 'moment';
import eachMinuteOfInterval from 'date-fns/eachMinuteOfInterval';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import parse from 'date-fns/parse';

import {
  ABBREVIATED_MONTH,
  MILITARY_TIME,
  HOUR_MINUTE_DAY_TIME
} from 'bento-ordering/base/constants/timeFormat';
import { getCartItem } from 'bento-ordering/cart/utils/getCartItem';

/**
 * Parses a string representation of a date object into a Date object with the format 'mm/dd/yyyy'.
 *
 * We are hardcoding the format: 'mm/dd/yyyy' because we know before hand that
 * that's the format returned by the function toLocaleDateString().
 *
 * @param {string} date a string representation of a date object
 * @returns a Date object parsed in the format 'mm/dd/yyyy'.
 */
const parseDate = date => {
  return parse(new Date(date).toLocaleDateString(), 'mm/dd/yyyy', new Date());
};

/**
 * Formats a Date object into the dateFormat passed.
 *
 * @param {string} date a string representation of a date object. Ex: 'Tue Mar 07 2023'
 * @param {*} dateFormat the date format used to format the date.
 * @returns a string with the formatted date.
 */
export const formatDate = (date, dateFormat) => {
  return format(parseDate(date), dateFormat);
};

export const getDateSelectOptionsForNumDays = numberOfDaysAvailable => {
  const today = moment();
  const availableDays = [...Array(numberOfDaysAvailable).keys()].map(x => {
    const day = new Date();
    day.setDate(day.getDate() + x);
    const isSameDay = moment(day).isSame(today, 'day');

    if (isSameDay) {
      return {
        label: 'Today',
        value: day.toDateString()
      };
    } else {
      return {
        label: `${moment(day).format(ABBREVIATED_MONTH)} ${day.getDate()}`,
        value: day.toDateString()
      };
    }
  });
  availableDays.unshift({ label: null, value: null });
  return availableDays;
};

export const roundToNearestQuarterHour = date => {
  /**
   * takes a date object and rounds its minutes up to
   * the nearest quarter hour
   * @param  {string} date date object
   * @return {date}
   */
  const minutes = date.getMinutes();
  let roundedMinutes = (Math.ceil(minutes / 15) * 15) % 60;
  if (minutes === 0) {
    roundedMinutes = 15;
  }
  date.setMinutes(roundedMinutes);
  if (roundedMinutes === 0 && minutes > 0) {
    date.setHours(date.getHours() + 1);
  }
  return date;
};

/**
 * timeIntervals is used in the GroupOrderDeadlineControl to return an array of objects
 * each object contains a label and value.
 * the label represents a time in am/pm format and the value is the same time in military format
 * the function checks the date passed to ensure if the day is today to only include times in the future
 *
 * @param  {string} day a string representation of a date object
 * @return {array}
 */
export const timeIntervals = day => {
  const intervals = [{ label: '', value: '' }];
  const date = new Date(day);
  const today = new Date();
  const isSameDay = date.getDate() === today.getDate();

  // we need to use todays date object here rather than the date passed to ensure
  // the tz and minute/second info is the same and most current passed to the interval
  // if the date passed is the same day as today
  const startInterval = isSameDay ? roundToNearestQuarterHour(today) : date;
  const endInterval = isSameDay ? endOfDay(today) : endOfDay(date);
  const interval = {
    start: startInterval,
    end: endInterval
  };

  eachMinuteOfInterval(interval, { step: 15 }).forEach(interval => {
    const time = format(interval, HOUR_MINUTE_DAY_TIME);
    intervals.push({ label: time, value: format(interval, MILITARY_TIME) });
  });
  return intervals;
};

export const isRoute = route => {
  const pathname = window.location.pathname;

  return pathname.includes(route);
};

const _convertStringToFloat = value => Number.parseFloat(value);

export const formatPresetSpendingLimits = (presetLimitOptions = null) => {
  let limitOptions;
  if (presetLimitOptions) {
    limitOptions = [{ value: '', label: 'None' }];

    presetLimitOptions.split(',').forEach(val => {
      const tempDict = {};
      tempDict.label = `$${val}`;
      tempDict.value = val;
      limitOptions.push(tempDict);
    });
  } else {
    limitOptions = [
      { value: '', label: 'None' },
      { value: '15', label: '$15' },
      { value: '20', label: '$20' },
      { value: '25', label: '$25' }
    ];
  }
  return limitOptions;
};

export const hasExceededSpendingLimit = (
  pricePreview,
  perPersonSpendinglimit,
  amountSpent,
  cartItemId,
  cartItems
) => {
  let result = false;
  if (perPersonSpendinglimit) {
    const spendingLimit = _convertStringToFloat(perPersonSpendinglimit);
    let subTotal = amountSpent ? _convertStringToFloat(amountSpent) : 0;
    const pricePreviewResult = pricePreview
      ? _convertStringToFloat(pricePreview)
      : 0;

    if (cartItemId) {
      const cartItem = getCartItem(cartItemId, cartItems);
      subTotal -= cartItem ? _convertStringToFloat(cartItem?.total ?? 0) : 0;
    }
    result = subTotal + pricePreviewResult > spendingLimit;
  }
  return result;
};

export const formatExistingDateString = dateString => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  return date.toDateString();
};

export const formatTimeFromDateString = dateString => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  const hours = date
    .getHours()
    .toString()
    .padStart(2, '0');
  const minutes = date
    .getMinutes()
    .toString()
    .padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Calculates the difference between the current time and a specified date and time.
 *
 * @param {string} dateString - A string representing the date and time to compare to the current time.
 * @returns {number} - The difference between the current time and the specified date and time, in milliseconds.
 */
export const differenceBetweenTime = dateString => {
  const now = new Date();
  const datetimeObj = new Date(dateString);
  return datetimeObj.getTime() - now.getTime();
};
